/**
 * 精灵图互动宠物 - 主组件
 * 组合所有子系统：精灵图管理、去背、状态机、拖拽、渲染
 */
import { useState, useCallback, useRef } from 'react'
import { useThemeStore } from '../../../stores/themeStore'
import { useSpriteStore } from './useSpriteStore'
import { useSpriteBackground } from './useSpriteBackground'
import { usePetStateMachine } from './usePetStateMachine'
import { useDraggable } from './useDraggable'
import SpriteRenderer from './SpriteRenderer'
import SpriteGalleryModal from './SpriteGalleryModal'
import { DISPLAY_SIZE } from './types'
import './SpritePet.css'

interface SpritePetProps {
  enabled?: boolean
  externalGalleryOpen?: boolean
  onExternalGalleryClose?: () => void
  onPetEnabledChange?: (enabled: boolean) => void
}

const SpritePet = ({
  enabled = true,
  externalGalleryOpen = false,
  onExternalGalleryClose,
  onPetEnabledChange,
}: SpritePetProps) => {
  const { theme } = useThemeStore()
  const isDark = theme === 'dark'

  // 精灵图源管理
  const {
    sprites,
    activeId,
    spriteSrc,
    isLoading: isStoreLoading,
    addSprite,
    removeSprite,
    renameSprite,
    setActive,
    resetToDefault,
  } = useSpriteStore()

  // Canvas 去背处理
  const { processedSrc, isProcessing, progress } = useSpriteBackground(spriteSrc)

  // 状态机
  const {
    animationState,
    handleClick,
    handleDoubleClick,
    handleHover,
    handleDragStart,
    handleDragEnd,
    handleAnimationEnd,
  } = usePetStateMachine()

  // 拖拽系统
  const { position, isDragging, hasDragged, dragRef, handlePointerDown } = useDraggable({
    elementSize: DISPLAY_SIZE,
    onDragStart: handleDragStart,
    onDragEnd: handleDragEnd,
  })

  // 替换弹窗状态
  const [modalOpen, setModalOpen] = useState(false)

  // 点击计时器（区分单击和双击）
  const clickTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // 处理鼠标松开（区分点击和拖拽）
  const handleMouseUp = useCallback(() => {
    // 拖拽过程中不触发点击
    if (hasDragged.current) return
  }, [hasDragged])

  // 处理单击（延迟执行，等待双击检测）
  const handleSingleClick = useCallback(
    (e: React.MouseEvent) => {
      if (hasDragged.current || isDragging) return
      e.preventDefault()

      if (clickTimerRef.current) {
        clearTimeout(clickTimerRef.current)
        clickTimerRef.current = null
        // 双击
        handleDoubleClick()
      } else {
        clickTimerRef.current = setTimeout(() => {
          clickTimerRef.current = null
          handleClick()
        }, 250)
      }
    },
    [hasDragged, isDragging, handleClick, handleDoubleClick]
  )

  // 右键菜单 → 打开替换弹窗
  const handleContextMenu = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    setModalOpen(true)
  }, [])

  // 合并内部和外部弹窗状态
  const isModalOpen = modalOpen || externalGalleryOpen

  const handleModalClose = useCallback(() => {
    setModalOpen(false)
    onExternalGalleryClose?.()
  }, [onExternalGalleryClose])

  // 加载中或处理中
  const isLoading = isStoreLoading || isProcessing

  return (
    <>
      {enabled && (
        <div
          ref={dragRef}
          className={`sprite-pet-container ${isDark ? 'dark' : 'light'} ${isDragging ? 'dragging' : ''}`}
          style={{
            left: position.x,
            top: position.y,
          }}
          onMouseDown={handlePointerDown}
          onTouchStart={handlePointerDown}
          onMouseUp={handleMouseUp}
          onClick={handleSingleClick}
          onMouseEnter={handleHover}
          onContextMenu={handleContextMenu}
        >
          {isLoading || !processedSrc ? (
            <div className="sprite-pet-loading">
              <div className="sprite-pet-progress-bar">
                <div
                  className="sprite-pet-progress-fill"
                  style={{ width: `${progress}%` }}
                />
              </div>
              <span className="sprite-pet-progress-text">{progress}%</span>
            </div>
          ) : (
            <SpriteRenderer
              spriteSrc={processedSrc}
              animationState={animationState}
              onAnimationEnd={handleAnimationEnd}
            />
          )}
        </div>
      )}

      <SpriteGalleryModal
        open={isModalOpen}
        onClose={handleModalClose}
        sprites={sprites}
        activeId={activeId}
        isLoading={isStoreLoading}
        onAddSprite={addSprite}
        onRemoveSprite={removeSprite}
        onRenameSprite={renameSprite}
        onSetActive={setActive}
        onResetToDefault={resetToDefault}
        isDark={isDark}
        petEnabled={enabled}
        onPetEnabledChange={onPetEnabledChange}
      />
    </>
  )
}

export default SpritePet
